import { CampaignSendDetail, CampaignSendStatus, CampaignStatus } from './types';

// Timestamps are honest UTC at rest; the portal displays IST regardless of
// the viewer's machine timezone.
const IST = 'Asia/Kolkata';

const dayTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
});
const timeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true,
});
const dayFmt = new Intl.DateTimeFormat('en-IN', { timeZone: IST, month: 'short', day: 'numeric' });
const istDayKey = new Intl.DateTimeFormat('en-CA', { timeZone: IST, dateStyle: 'short' });

export const fmtDayTime = (iso: string) => dayTimeFmt.format(new Date(iso));
export const fmtTime = (iso: string) => timeFmt.format(new Date(iso));
export const fmtDay = (iso: string) => dayFmt.format(new Date(iso));

/** "Jul 13 · 10:02 am → 10:40 am" (read time collapses to time-only when same IST day). */
export const fmtSentRead = (sentAt: string | null, readAt: string | null): string => {
  if (!sentAt) return '—';
  const sent = fmtDayTime(sentAt);
  if (!readAt) return `${sent} → —`;
  const sameDay = istDayKey.format(new Date(sentAt)) === istDayKey.format(new Date(readAt));
  return `${sent} → ${sameDay ? fmtTime(readAt) : fmtDayTime(readAt)}`;
};

/** Chatbot-DB timestamps are IST-naive — pin them to +05:30 before comparing with UTC. */
export const parseChatTime = (dateTime: string): Date => {
  const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateTime);
  return new Date(hasZone ? dateTime : `${dateTime}+05:30`);
};

export const CAMPAIGN_CHIP: Record<CampaignStatus, string> = {
  Draft: 'bg-charcoal/5 text-graphite border-charcoal/20',
  Active: 'bg-forest/10 text-forest border-forest/40',
  Paused: 'bg-amber-ink/10 text-amber-ink border-amber-ink/30',
  Completed: 'bg-sky-wash/30 text-[#2c6479] border-[#2c6479]/25',
  Aborted: 'bg-brick/10 text-brick border-brick/30',
};

export const PRODUCER_LABEL: Record<string, string> = {
  campaign: 'campaign', cadence: 'cadence', operator: 'operator', system: 'system', test: 'owner probe',
};
export const PRODUCER_PILL: Record<string, string> = {
  campaign: 'border-sky-wash bg-sky-wash/30 text-[#2c6479]',
  cadence: 'border-sage bg-sage/20 text-forest',
  operator: 'border-sketch bg-charcoal/5 text-graphite',
  system: 'border-sketch bg-charcoal/5 text-graphite',
  test: 'border-gold/60 bg-gold/10 text-amber-ink',
};

export const SEND_PILL: Record<CampaignSendStatus, string> = {
  scheduled: 'bg-charcoal/5 text-graphite',
  sent: 'bg-sky-wash/40 text-[#2c6479]',
  delivered: 'bg-sky-wash/40 text-[#2c6479]',
  read: 'bg-forest/10 text-forest',
  failed: 'bg-brick/10 text-brick',
  skipped: 'bg-amber-ink/10 text-amber-ink',
};

const META_ERROR_LABELS: Record<number, string> = {
  131049: 'marketing limit',
  131050: 'opted out of marketing',
  131026: 'number unavailable',
  131047: 're-engagement window closed',
  131048: 'spam rate limit',
  132015: 'template paused',
  132016: 'template disabled',
  132000: 'blanks left unfilled', // a bare {{n}} shape sent without its values
};

export const errorLabel = (code: number): string =>
  META_ERROR_LABELS[code] ? `${META_ERROR_LABELS[code]} (${code})` : `error ${code}`;

/** Plain-language reason line under a failed/skipped status pill. */
export const sendIssueText = (send: CampaignSendDetail): string | null => {
  if (send.status === 'failed') {
    if (send.error_code) return errorLabel(send.error_code);
    return send.error_detail || 'failed';
  }
  if (send.status === 'skipped') {
    const detail = send.error_detail || '';
    if (detail.startsWith('task_cancelled:')) {
      return `cancelled — ${detail.slice('task_cancelled:'.length).replace(/_/g, ' ')}`;
    }
    return detail || 'skipped';
  }
  return null;
};

export const pct = (value: number, denominator: number): string =>
  denominator ? `${Math.round((value / denominator) * 100)}%` : '0%';

/** "2m 28s" / "45s" — em-dash when nothing was timed. */
export const fmtDwell = (ms: number): string => {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
};

/** Video load latency: sub-second in ms, else one-decimal seconds. */
export const fmtLoad = (ms: number | null): string =>
  ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

/** Today's IST day as YYYY-MM-DD (the portal displays IST regardless of machine TZ). */
export const istToday = (): string => istDayKey.format(new Date());

/** YYYY-MM-DD ± days (date arithmetic on the day key, no timezone drift). */
export const shiftDay = (day: string, delta: number): string => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

export const fmtINR = (n: number): string =>
  `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** An inclusive IST day range as the half-open UTC instants a server
 *  `since`/`until` filter takes: [from 00:00 IST, the day after `to` 00:00 IST). */
export const istDayBounds = (from: string, to: string): { since: string; until: string } => ({
  since: new Date(`${from}T00:00:00+05:30`).toISOString(),
  until: new Date(`${shiftDay(to, 1)}T00:00:00+05:30`).toISOString(),
});

/** A UTC timestamp's IST day as YYYY-MM-DD (for date-range comparisons). */
export const istDay = (iso: string): string => istDayKey.format(new Date(iso));

// The one ladder (core lead_stages.STAGES), in the order the chips read: closest to a visit first.
export const STAGE_ORDER = ['booked', 'negotiation', 'revisited', 'revisit_scheduled', 'visited', 'visit_scheduled', 'visit_intent', 'followup', 'engaged', 'contacted', 'attempting', 'new',
  'deferred', 'gone_quiet', 'unreachable', 'unknown', 'lost', 'disqualified'];
export const STAGE_LABEL: Record<string, string> = {
  booked: 'booked', negotiation: 'negotiation', revisited: 're-visited', revisit_scheduled: 're-visit sched',
  visited: 'visited', visit_scheduled: 'visit sched', visit_intent: 'visit intent', followup: 'follow-up',
  engaged: 'engaged', contacted: 'contacted', attempting: 'attempting', new: 'new', deferred: 'deferred',
  gone_quiet: 'gone quiet', unreachable: 'unreachable', unknown: 'unknown', lost: 'lost', disqualified: 'disq',
};
export const stageCls = (st: string): string =>
  ['visited', 'visit_scheduled', 'revisit_scheduled', 'revisited', 'negotiation', 'booked'].includes(st) ? 'bg-forest text-warm-white border-forest'
    : st === 'visit_intent' ? 'bg-gold/15 border-gold/60'
      : st === 'engaged' || st === 'followup' ? 'bg-sage/25 border-sage'
        : st === 'lost' || st === 'disqualified' || st === 'unreachable' || st === 'gone_quiet' ? 'text-graphite border-sketch-soft'
          : 'border-sketch-soft';
